import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listAuditTrail, type AuditEventRow } from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import css from "./WarehouseAdminPage.module.css";

/**
 * The user-action audit trail (backlog F1, ADR 267).
 *
 * Exists from the first slice rather than after the writers are complete: a
 * trail nobody can read during a customer call is not a trail, and "query the
 * database" is not an answer anyone can give on a call.
 *
 * The detail column is the reason this is a table of expandable rows rather than
 * a flat grid. For a delete it holds the subject's own fields captured before it
 * went — after the delete, that JSON is the only description of the thing that
 * exists anywhere — so it must be readable, but it would drown the list if it
 * were always open.
 */
export function AuditPage() {
  const { t, i18n } = useTranslation();
  const sites = useSiteStore((s) => s.availableSites);
  const [siteId, setSiteId] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["audit-trail", siteId],
    queryFn: () => listAuditTrail(siteId || undefined),
    retry: false,
  });

  // Audit timestamps are real server instants describing when a person acted, so
  // they render in the reader's own zone — unlike a run's floor times, which are
  // facts about a warehouse and stay on its clock (ADR 265/266).
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language);

  return (
    <div className={css.root}>
      <div className={css.header}>
        <h1 className={css.title}>{t("audit.title")}</h1>
      </div>
      <p className={css.notice}>{t("audit.intro")}</p>

      <div className={css.form}>
        <div className={css.field}>
          <label className={css.label} htmlFor="audit-site">{t("audit.site")}</label>
          <select id="audit-site" className={css.input} value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}>
            <option value="">{t("audit.allSites")}</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {query.isLoading && <p className={css.notice}>{t("common.loading")}</p>}

      {/* 403 and "nothing yet" are different answers and must not look alike:
          one means the trail is not yours to read, the other that it is empty. */}
      {query.isError && (
        <p className={css.notice} data-testid="audit-error">{t("audit.denied")}</p>
      )}

      {query.data?.length === 0 && (
        <p className={css.notice} data-testid="audit-empty">{t("audit.empty")}</p>
      )}

      {!!query.data?.length && (
        <table className={css.table} data-testid="audit-table">
          <thead>
            <tr>
              <th>{t("audit.col.ts")}</th>
              <th>{t("audit.col.actor")}</th>
              <th>{t("audit.col.action")}</th>
              <th>{t("audit.col.entity")}</th>
              <th>{t("audit.col.site")}</th>
              <th>{t("audit.col.outcome")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.map((e) => (
              <Row key={e.id} e={e} fmt={fmt}
                   open={open === e.id}
                   onToggle={() => setOpen(open === e.id ? null : e.id)} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Row({ e, fmt, open, onToggle }: {
  e: AuditEventRow; fmt: (iso: string) => string; open: boolean; onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <tr>
        <td>{fmt(e.ts)}</td>
        {/* The name captured at write time; the sub when there was no name to
            capture. Never re-resolved — a deleted user must still be named. */}
        <td>{e.actorName ?? e.actorSub ?? t("audit.unknownActor")}</td>
        <td><code>{e.action}</code></td>
        <td><code>{e.entityType}/{e.entityId}</code></td>
        <td>{e.siteId ?? "—"}</td>
        <td>
          {/* Outcome carries a word, not only a colour — the design system
              forbids communicating state by colour alone. */}
          <span className={css.badge}>{t(`audit.outcome.${e.outcome}`)}</span>
        </td>
        <td>
          {e.detail && (
            <button type="button" className={css.linkBtn} onClick={onToggle}
                    aria-expanded={open}>
              {open ? t("audit.hideDetail") : t("audit.showDetail")}
            </button>
          )}
        </td>
      </tr>
      {open && e.detail && (
        <tr>
          <td colSpan={7}>
            <pre data-testid={`audit-detail-${e.id}`}>{pretty(e.detail)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Pretty-print when it parses; show it verbatim when it does not. A detail that
 *  failed to serialise is itself a finding, and hiding it would erase that. */
function pretty(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
